import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useListenSocket } from "../../hooks/useListenSocket";
import { useAudioPlayer, type AudioChunk } from "../../hooks/useAudioPlayer";
import { useSpeechOut } from "../../hooks/useSpeechOut";
import { directionOf, t, type UiLang } from "../../i18n/strings";

const langLabels: Record<string, string> = { en: "English", ar: "العربية", fr: "Français" };

function uiLangFor(code: string): UiLang {
  return code === "ar" || code === "fr" ? code : "en";
}

export default function ListenPage() {
  const { code = "", lang = "" } = useParams<{ code: string; lang: string }>();
  const navigate = useNavigate();
  const uiLang = uiLangFor(lang);
  const dir = directionOf(lang);

  const [audioOn, setAudioOn] = useState(false);
  // The audio handler is filled in below, once the player exists (it needs `serverAudio` from the socket).
  const audioHandler = useRef<(chunk: AudioChunk) => void>(() => undefined);
  const { connState, sessionStatus, captions, partial, joinError, serverAudio } = useListenSocket(code, lang, {
    enabled: audioOn,
    onAudio: (chunk) => audioHandler.current(chunk),
  });

  // Two ways to hear the captions:
  //  - server audio: a natural neural voice, synthesized once on the server and streamed to phones that turned sound on;
  //  - the phone's own built-in voice: used when the server can't speak (no speech service configured),
  //    and for any single caption the server failed to synthesize.
  const useServerVoice = serverAudio;
  const { speak, supported: speechSupported } = useSpeechOut(lang, audioOn);
  const player = useAudioPlayer({ enabled: audioOn && useServerVoice, onFallback: speak });
  audioHandler.current = player.enqueue;

  const lastSpokenRef = useRef<string | null>(null);
  const feedRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (useServerVoice) return; // the server voice handles it
    const latest = captions[captions.length - 1];
    if (!latest || latest.isSource) return;
    if (latest.utteranceId === lastSpokenRef.current) return;
    lastSpokenRef.current = latest.utteranceId;
    speak(latest.text);
  }, [captions, speak, useServerVoice]);

  // Every caption this socket receives is already in the language the attendee
  // chose: translated text for a target language, or the speaker's own words
  // (isSource) for someone who picked the source language. Filtering out
  // isSource here left source-language listeners staring at an empty feed.
  // Sorting by sequence number keeps sentences in spoken order even when a
  // slow translation lets a later sentence arrive first.
  const targetCaptions = [...captions].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
  const latestId = targetCaptions[targetCaptions.length - 1]?.utteranceId;

  const statusPill = (() => {
    if (connState === "connected") return { cls: "ok", label: t(uiLang, "connected") };
    if (connState === "reconnecting") return { cls: "warn", label: t(uiLang, "reconnecting") };
    if (connState === "connecting") return { cls: "warn", label: t(uiLang, "connecting") };
    return { cls: "bad", label: t(uiLang, "disconnected") };
  })();

  return (
    <div className="attendee-screen" dir={dir} style={{ height: "100vh", paddingBottom: 16 }}>
      <div className="listen-header">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src="/brand/afa-logo.png" alt="" aria-hidden="true" className="listen-logo" />
          <div>
            <div className="session-name">{code}</div>
            <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{langLabels[lang] ?? lang}</div>
          </div>
        </div>
        <span className={`status-pill ${statusPill.cls}`}>
          <span className="status-dot" />
          {statusPill.label}
        </span>
      </div>

      {joinError && <div className="error-banner">{joinError}</div>}

      {!joinError && (
        <>
          {sessionStatus === "ENDED" && <div className="card empty-state">{t(uiLang, "sessionEnded")}</div>}
          {sessionStatus === "PAUSED" && <div className="card empty-state">{t(uiLang, "sessionPaused")}</div>}

          <div className="caption-feed" ref={feedRef}>
            {targetCaptions.length === 0 && !partial && sessionStatus !== "ENDED" && (
              <div className="empty-state">{t(uiLang, "waitingForSpeaker")}</div>
            )}
            {/* Newest first (the feed is column-reverse): the phrase being spoken right
                now, updating in place until its permanent caption replaces it. Never spoken aloud. */}
            {partial && (
              <div key="live" className="caption-bubble partial" aria-live="polite">
                {partial.text}
              </div>
            )}
            {[...targetCaptions].reverse().map((c) => (
              <div
                key={`${c.utteranceId}`}
                className={`caption-bubble${c.utteranceId === latestId && !partial ? " latest" : ""}`}
              >
                {c.text}
              </div>
            ))}
          </div>

          <div className="listen-footer">
            {(serverAudio || speechSupported) && (
              <button
                className="btn-secondary"
                onClick={() => {
                  // Mobile browsers only allow sound after a tap: unlock playback right here, inside the tap.
                  if (!audioOn && serverAudio) player.unlock();
                  setAudioOn((v) => !v);
                }}
              >
                {audioOn ? t(uiLang, "audioOn") : t(uiLang, "audioOff")}
              </button>
            )}
            <button className="btn-ghost" onClick={() => navigate(`/join/${code}`)}>
              {t(uiLang, "changeLanguage")}
            </button>
            <button className="btn-ghost" onClick={() => navigate("/join")}>
              {t(uiLang, "leave")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
